export type StepStatus = "approved" | "current" | "pending" | "rejected";

export interface IApprover {
  firstName: string;
  lastName: string;
  profileImage: string;
  isHeadOffice?: boolean;
  employeeId?: string;
  department?: string;
  position?: string;
  branch?: string;
}

// A member of a parallel-approval gate: an approver plus their own decision.
export interface IParallelApprover extends IApprover {
  role?: string | null;
  status?: string;
}

export interface IWorkflowStep {
  id: number;
  stepOrder: number;
  nodeName: string;
  positionName: string;
  approver: IApprover | null;
  // Graph node kind ("parallel_approval" renders `approvers` instead of a
  // single `approver"). Undefined on legacy linear steps.
  type?: string | null;
  approvers?: IParallelApprover[] | null;
  status: StepStatus;
  branch?: string | null;
  actionAt?: string | null;
  comments?: string | null;
}

export interface ILeaveRequestFlow {
  user: {
    firstName: string;
    lastName: string;
    profileImage: string;
  };
  leaveType: string;
  startDate: string;
  endDate: string;
  workflow: {
    steps: IWorkflowStep[];
  };
}

export const STATUS_STYLES: Record<
  StepStatus,
  {
    border: string;
    headerBg: string;
    headerText: string;
    iconBg: string;
    iconColor: string;
    disabled: boolean;
  }
> = {
  approved: {
    border: "border-green-400",
    headerBg: "bg-green-500",
    headerText: "text-white",
    iconBg: "bg-green-50",
    iconColor: "text-green-500",
    disabled: false,
  },
  current: {
    border: "border-orange-500",
    headerBg: "bg-orange-500",
    headerText: "text-white",
    iconBg: "bg-orange-50",
    iconColor: "text-orange-500",
    disabled: false,
  },
  pending: {
    border: "border-border",
    headerBg: "bg-gray-100",
    headerText: "text-gray-400",
    iconBg: "bg-gray-100",
    iconColor: "text-gray-400",
    disabled: true,
  },
  rejected: {
    border: "border-red-400",
    headerBg: "bg-red-500",
    headerText: "text-white",
    iconBg: "bg-red-50",
    iconColor: "text-red-500",
    disabled: false,
  },
};

/** Lookup KEYS, not display text — a module-scope Record cannot call a hook.
 *  The consumer (step-node) resolves these with `t()`. */
export const STATUS_LABEL: Record<StepStatus, string> = {
  approved: "common:status.approved",
  current: "my-space:labels.current",
  pending: "common:status.pending",
  rejected: "common:status.rejected",
};

/** Every node is forced to this exact height so all arms are equal. */
export const NODE_HEIGHT = 280;

/** Space between nodes = NODE_HEIGHT + ARM_GAP, so arm length = ARM_GAP always. */
export const ARM_GAP = 80;
export const NODE_SPACING = NODE_HEIGHT + ARM_GAP; // 300
